// src/pages/AfficherFacture.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";

import { SocieteContext } from "../context/SocieteContext";
import FooterApercu from "../components/FooterApercu";
import TableauStyleVertical from "../components/TableauStyleVertical";
import BlocTotaux from "../components/BlocTotaux";
import BoutonOnglet from "../components/BoutonOnglet";
import { buildPdfBlobWithLastFooterAndPages } from "../utils/pdf";
import { notify } from "../ui/notify";

import DOMPurify from "dompurify";
import { revokeAllProtectedUrls, hydrateProtectedHtml } from "../utils/protectedFiles";
import "../index.css";
import { loadTableTheme, themeStyleVars, onThemeChange } from "../utils/tableTheme";

/* ---------- helpers (mêmes signatures que AfficherDevis) ---------- */
const safeJSON = (x, fb = {}) => {
    try {
        return typeof x === "string" ? JSON.parse(x) : (x ?? fb);
    } catch {
        return fb;
    }
};
const r2 = (n) => Number((Number(n || 0)).toFixed(2));
const fmt2 = (n) =>
    new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(n || 0));
const toCurrency = (v) => fmt2(v) + " €";
const fmtPct = (n) =>
    new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(n || 0));
const totalLigne = (l) =>
    (parseFloat(l.quantite || 0) * parseFloat(l.prixUnitaire || 0)) || 0;
const pxNum = (n, fb) => (Number.isFinite(Number(n)) ? Number(n) : fb);

// --- URLs sécurisées pour images (identique AfficherDevis)
const API_ORIGIN = (() => {
    const b = axiosInstance?.defaults?.baseURL || "http://localhost:5000/api";
    try {
        return new URL(b).origin;
    } catch {
        return "http://localhost:5000";
    }
})();
const getAuthToken = () => {
    const auth = axiosInstance?.defaults?.headers?.common?.Authorization;
    if (auth?.startsWith("Bearer ")) return auth.slice(7);
    try {
        return (
            localStorage.getItem("token") ||
            localStorage.getItem("authToken") ||
            localStorage.getItem("jwt") ||
            ""
        );
    } catch {
        return "";
    }
};
const withToken = (p) => {
    if (!p) return p;
    const u = new URL(p, API_ORIGIN);
    const t = getAuthToken();
    if (t) u.searchParams.set("t", t);
    return u.toString();
};

// px -> mm + style boîte (px à l’écran / mm en PDF)
const MM_PER_PX = 25.4 / 96;
const pxToMm = (px) =>
    Math.round(Number(px || 0) * MM_PER_PX * 100) / 100;
const boxStyle = (isPdf, box = {}, z = 1) => {
    const { x, y, width, height } = box;
    if (!isPdf) {
        return {
            position: "absolute",
            left: `${x}px`,
            top: `${y}px`,
            ...(width != null ? { width: `${width}px` } : {}),
            ...(height != null ? { height: `${height}px` } : {}),
            zIndex: z,
            pointerEvents: "none",
            background: "transparent",
        };
    }
    return {
        position: "absolute",
        left: `${pxToMm(x)}mm`,
        top: `${pxToMm(y)}mm`,
        ...(width != null ? { width: `${pxToMm(width)}mm` } : {}),
        ...(height != null ? { height: `${pxToMm(height)}mm` } : {}),
        zIndex: z,
        pointerEvents: "none",
        background: "transparent",
    };
};

function formatDateFR(raw) {
    if (!raw) return "-";
    const s =
        typeof raw === "string"
            ? raw.slice(0, 10)
            : new Date(raw).toISOString().slice(0, 10);
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
}

/* Adresse client : “N°/Rue” + “CP Ville” sur la ligne suivante */
const splitClientAddr = (obj = {}) => {
    const addrRaw =
        (obj.client_adresse ||
            obj.numero_voie ||
            obj.voie ||
            obj.rue ||
            obj.adresse ||
            ""
        ).trim();
    const m = addrRaw.match(/^(.*?)(?:,\s*)?(\d{4,5})\s+(.+)$/i);
    const ligne1 = (m ? m[1] : addrRaw).trim();
    const cp = (obj.code_postal || obj.cp || obj.postal_code || (m ? m[2] : "")).trim();
    const ville = (obj.ville || obj.commune || obj.city || (m ? m[3] : "")).trim();
    return { ligne1, cp, ville };
};

// === ActionsEmailFacture (aligné sur ActionsEmailDevis) ===
function ActionsEmailFacture({
  facture,
  pdfElRef,
  footerElRef,
  onPreparePdfLayout,
  onCleanupPdfLayout,
}) {
  const [open, setOpen] = useState(false);

  // destinataires
  const [to, setTo] = useState(facture?.client_email || "");
  const [cc, setCc] = useState("");

  // modèles depuis EmailTemplates (fallback si le back ne renvoie rien)
  const [tpls, setTpls] = useState({
    initial: {
      subject: "Votre facture {{numero}}",
      body:
        "Bonjour {{client_nom}},\n" +
        "Veuillez trouver votre facture {{numero}} en pièce jointe (montant TTC : {{total_ttc}} €).\n\n" +
        "Cordialement,\n{{societe_nom}}",
    },
    relance: {
      subject: "Relance – facture {{numero}}",
      body:
        "Bonjour {{client_nom}},\n" +
        "Sauf erreur de notre part, la facture {{numero}} (montant TTC : {{total_ttc}} €) reste en attente de règlement.\n\n" +
        "Cordialement,\n{{societe_nom}}",
    },
  });

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("initial"); // "initial" | "relance"
  const [attachPdf, setAttachPdf] = useState(true);

  // historique local (persistant)
  const storageKey = `mailHistory:facture:${facture?.id}`;
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch {
      return [];
    }
  });

  const fmt2 = (n) =>
    new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const values = useMemo(() => {
    const numero = facture?.numero_facture || facture?.numero || "";
    const client_nom = facture?.client_nom || "";
    const societe_nom = facture?.societe_nom || "FlowDevis";
    const total_ttc =
      facture?.total_ttc != null ? fmt2(facture.total_ttc) : "";
    return { numero, client_nom, societe_nom, total_ttc };
  }, [facture]);

  const renderTpl = (s = "", v = {}) =>
    String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => v[k] ?? "");

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () =>
        resolve(String(r.result).split(",")[1] || "");
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  async function buildPdfAttachment() {
    if (!attachPdf) return null;

    const rootEl = pdfElRef?.current;
    const footerEl = footerElRef?.current;
    if (!rootEl) throw new Error("a4-page introuvable");

    // on laisse le parent gérer les classes / layout
    await onPreparePdfLayout?.();

    try {
      const filename = `facture-${
        facture?.numero_facture || facture?.numero || facture?.id || ""
      }.pdf`;

      const blob = await buildPdfBlobWithLastFooterAndPages(
        rootEl,
        footerEl,
        { filename }
      );

      const contentBase64 = await blobToBase64(blob);
      return { filename, contentBase64 };
    } finally {
      await onCleanupPdfLayout?.();
    }
  }

  async function send() {
    const kind = mode; // "initial" ou "relance"
    try {
      const email = String(to || "").trim();
      const ccStr = String(cc || "").trim();
      const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!looksValid) {
        notify.warning({
          title: "Adresse e-mail invalide",
          message: "Renseigne une adresse valide dans “À”.",
        });
        return;
      }

      const tpl = kind === "relance" ? tpls.relance : tpls.initial;
      const subj = subject?.trim() || renderTpl(tpl.subject, values);
      const body = message?.trim() || renderTpl(tpl.body, values);

      notify.info({
        title: "Préparation",
        message: "Génération du PDF…",
      });
      const attachment = await buildPdfAttachment();

      await axiosInstance.post("/emails/send-document", {
        type: "facture",
        id: Number(facture?.id),
        to: email,
        cc: ccStr || undefined,
        subject: subj,
        message: body,
        attachment,
      });

      notify.success({
        title: "E-mail envoyé",
        message: `Facture ${values.numero} (${
          kind === "relance" ? "relance" : "initiale"
        }) envoyée à ${email}.`,
      });

      const entry = {
        ts: Date.now(),
        kind,
        to: email,
        cc: ccStr || "",
        subject: subj,
      };
      const next = [entry, ...history].slice(0, 50);
      setHistory(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    } catch (err) {
      console.error("[send-facture-email]", err);
      notify.error({
        title: "Échec de l’envoi",
        message: "Impossible d’envoyer l’e-mail. Réessaie dans un instant.",
      });
    }
  }

  const sendBtnLabel =
    mode === "relance" ? "Envoyer une relance" : "Envoyer la facture";

  // charge les templates à l’ouverture (simple, scope=facture)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axiosInstance.get("/email-templates", {
          params: { scope: "facture" },
        });
        if (data?.initial || data?.relance) {
          setTpls((o) => ({ ...o, ...data }));
        }
      } catch (e) {
        console.warn("[email-templates facture] fallback défaut:", e);
      } finally {
        const tpl = mode === "relance" ? tpls.relance : tpls.initial;
        setSubject(renderTpl(tpl.subject, values));
        setMessage(renderTpl(tpl.body, values));
      }
    })();
  }, [open]);

  // quand on change de mode, on ré-initialise sujet/corps depuis le modèle
  useEffect(() => {
    if (!open) return;
    const tpl = mode === "relance" ? tpls.relance : tpls.initial;
    setSubject(renderTpl(tpl.subject, values));
    setMessage(renderTpl(tpl.body, values));
  }, [
    mode,
    tpls.initial.subject,
    tpls.initial.body,
    tpls.relance.subject,
    tpls.relance.body,
  ]);

  return (
    <>
      <BoutonOnglet actif={false} onClick={() => setOpen(true)}>
        Envoyer par e-mail
      </BoutonOnglet>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-sky-800/40">
          <div className="bg-white rounded-xl shadow-xl p-5 w-[900px] max-w-[95vw]">
            {/* Historique (local simple) */}
            <div className="mb-4 rounded bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700 max-h-24 overflow-auto">
              <div className="font-semibold mb-1">
                Historique des envois – {values.numero}
              </div>
              {history.length === 0 ? (
                <div>Aucun envoi enregistré.</div>
              ) : (
                history.map((h, i) => (
                  <div
                    key={i}
                    className="flex justify-between gap-3 py-0.5"
                  >
                    <span>{new Date(h.ts).toLocaleString()}</span>
                    <span>
                      {h.kind === "relance" ? "Relance" : "Initial"} → {h.to}
                      {h.cc ? ` (Cc: ${h.cc})` : ""}
                    </span>
                    <span
                      className="truncate max-w-[45%]"
                      title={h.subject}
                    >
                      {h.subject}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Envoyer la facture par e-mail
              </h3>

              <div className="inline-flex rounded-full bg-slate-100 p-1 text-sm">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full ${
                    mode === "initial" ? "bg-white shadow" : "opacity-70"
                  }`}
                  onClick={() => setMode("initial")}
                >
                  Facture initiale
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full ${
                    mode === "relance" ? "bg-white shadow" : "opacity-70"
                  }`}
                  onClick={() => setMode("relance")}
                >
                  Relance facture
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Colonne édition */}
              <div>
                <label className="block text-sm mb-1">À</label>
                <input
                  className="w-full border rounded px-3 py-2 mb-3"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />

                <label className="block text-sm mb-1">
                  Cc (optionnel)
                </label>
                <input
                  className="w-full border rounded px-3 py-2 mb-3"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                />

                <label className="block text-sm mb-1">Sujet</label>
                <input
                  className="w-full border rounded px-3 py-2 mb-3"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />

                <label className="block text-sm mb-1">Message</label>
                <textarea
                  className="w-full border rounded px-3 py-2 h-48"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />

                <label className="inline-flex items-center gap-2 mt-3">
                  <input
                    type="checkbox"
                    checked={attachPdf}
                    onChange={(e) => setAttachPdf(e.target.checked)}
                  />
                  Joindre le PDF
                </label>
              </div>

              {/* Colonne aperçu modèle */}
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Modèle utilisé :{" "}
                  {mode === "relance"
                    ? "Relance facture"
                    : "Facture initiale"}
                </div>
                <div className="text-sm font-semibold mb-2">
                  {renderTpl(
                    mode === "relance"
                      ? tpls.relance.subject
                      : tpls.initial.subject,
                    values
                  )}
                </div>
                <pre className="text-xs whitespace-pre-wrap leading-5">
                  {renderTpl(
                    mode === "relance"
                      ? tpls.relance.body
                      : tpls.initial.body,
                    values
                  )}
                </pre>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-5">
              <button
                type="button"
                className="px-3 py-2 border rounded"
                onClick={() => setOpen(false)}
              >
                Fermer
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-sky-700 text-white rounded"
                onClick={send}
              >
                {sendBtnLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


export default function AfficherFacture() {
    const { infosSociete } = useContext(SocieteContext);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = searchParams.get("id");

    const [mentionsFontPx, setMentionsFontPx] = useState(12);
    const [delaiMentions, setDelaiMentions] = useState(null);
    const [facture, setFacture] = useState(null);
    const [mentionsSafeHtml, setMentionsSafeHtml] = useState("");

    const [tableTheme, setTableTheme] = useState(loadTableTheme());


    useEffect(() => {
        const onFocus = () => setTableTheme(loadTableTheme());
        window.addEventListener("focus", onFocus);
        const off = onThemeChange(setTableTheme);
        return () => {
            window.removeEventListener("focus", onFocus);
            off?.();
        };
    }, []);


    const isPdf =
        typeof document !== "undefined" &&
        (document.body.classList.contains("exporting-pdf") ||
            document.body.classList.contains("static-pdf"));
    const pdfRef = useRef(null);
    const footerRef = useRef(null);

    const ownerKey =
        infosSociete?.user_id ||
        infosSociete?.id ||
        infosSociete?.siret ||
        "no-owner";

    useEffect(() => {
        revokeAllProtectedUrls();
    }, [ownerKey]);

    /* ---------- charge la facture ---------- */
    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const { data } = await axiosInstance.get(`/factures/${id}`);
                setFacture(data);
            } catch (e) {
                console.error("Erreur chargement facture:", e);
                notify.error({
                    title: "Chargement échoué",
                    message: "Impossible de charger la facture.",
                });
            }
        })();
    }, [id]);

    /* ---------- mentions de facturation (fallback légales) ---------- */
    useEffect(() => {
        (async () => {
            try {
                let data;
                try {
                    data = (await axiosInstance.get("/mentionsfacturation")).data || {};
                } catch {
                    data = (await axiosInstance.get("/mentionslegales")).data || {};
                }

                const raw =
                    data?.mentions_facturation ??
                    data?.mentions_supplementaires_facture ??
                    data?.mentions_supplementaires ??
                    "";

                // 12px par défaut, on respecte la valeur si définie dans les mentions
                const fontPx = Number.isFinite(Number(data?.mentions_font_px))
                    ? Number(data.mentions_font_px)
                    : 12;
                setMentionsFontPx(fontPx);

                // Délai par défaut dans les mentions (nullable si absent)
                const d = Number(
                    data?.delai_paiement_jours ?? data?.delaiPaiementJours
                );
                setDelaiMentions(Number.isFinite(d) ? d : null);

                const clean = DOMPurify.sanitize((raw || "").trim());
                const hydrated = await hydrateProtectedHtml(clean);
                setMentionsSafeHtml(hydrated); // on garde le HTML tel quel
            } catch {
                setMentionsSafeHtml("");
                notify.error({
                    title: "Mentions indisponibles",
                    message: "Impossible de charger les mentions de facturation.",
                });
            }
        })();
    }, []);

    /* ---------- lignes + totaux ---------- */
    const lignes = useMemo(() => {
        if (!facture) return [];
        try {
            return Array.isArray(facture.contenu)
                ? facture.contenu
                : JSON.parse(facture.contenu || "[]");
        } catch {
            return [];
        }
    }, [facture]);

    const base = useMemo(() => {
        const totalHT = lignes.reduce((a, l) => a + totalLigne(l), 0);
        const totalTVA = lignes.reduce(
            (a, l) => a + totalLigne(l) * (parseFloat(l.tva || 0) / 100),
            0
        );
        return { totalHT, totalTVA, totalTTC: totalHT + totalTVA };
    }, [lignes]);

    // --- délai/échéance (TOP-LEVEL, pas dans un autre hook) ---
    const meta = safeJSON(facture?.meta, {});
    const delaiMeta = Number(meta?.delai_paiement_jours);
    const delaiUsed = Number.isFinite(delaiMeta)
        ? delaiMeta
        : Number.isFinite(delaiMentions)
            ? delaiMentions
            : null;

    const echeanceDate = useMemo(() => {
        if (!Number.isFinite(delaiUsed)) return null;
        const d = new Date(
            facture?.date_facture || facture?.date || new Date()
        );
        d.setDate(d.getDate() + delaiUsed);
        return d;
    }, [facture, delaiUsed]);

    const remiseCalc = useMemo(() => {
        const r = safeJSON(facture?.meta, {})?.remise;
        if (!r || Number(r.value) <= 0) {
            return {
                hasRemise: false,
                label: "",
                remiseHT: 0,
                remiseTVA: 0,
                netHT: base.totalHT,
                netTVA: base.totalTVA,
                netTTC: base.totalTTC,
            };
        }

        const mode = (r.mode || "percent").toLowerCase();
        const value = Number(r.value || 0);

        let ratio = 0;
        if (mode === "percent")
            ratio = Math.max(0, Math.min(100, value)) / 100;
        else if (mode === "ht")
            ratio =
                base.totalHT > 0 ? Math.min(value, base.totalHT) / base.totalHT : 0;
        else
            ratio =
                base.totalTTC > 0 ? Math.min(value, base.totalTTC) / base.totalTTC : 0;

        const remiseHT = r2(base.totalHT * ratio);
        const remiseTVA = r2(base.totalTVA * ratio);
        const label =
            mode === "percent"
                ? `Remise (${fmtPct(value)} %)`
                : mode === "ht"
                    ? `Remise (${toCurrency(value)} HT)`
                    : `Remise (${toCurrency(value)} TTC)`;

        return {
            hasRemise: ratio > 0,
            label,
            remiseHT,
            remiseTVA,
            netHT: r2(base.totalHT - remiseHT),
            netTVA: r2(base.totalTVA - remiseTVA),
            netTTC: r2(base.totalTTC - remiseHT - remiseTVA),
        };
    }, [facture, base]);

    /* ---------- bloc texte société ---------- */
    const rawBloc = (infosSociete?.bloc_html || "").trim();
    const [blocHtmlSafe, setBlocHtmlSafe] = useState("");
    useEffect(() => {
        (async () => {
            const clean = DOMPurify.sanitize(rawBloc);
            const hydrated = await hydrateProtectedHtml(clean);
            setBlocHtmlSafe(hydrated);
        })();
    }, [rawBloc]);

    // Position bloc texte — px à l’écran, converti en mm pendant export
    const posTexteRaw = safeJSON(
        infosSociete?.positiontexte,
        infosSociete?.positiontexte
    );
    const posTexte = {
        x: pxNum(posTexteRaw?.x, 40),
        y: pxNum(posTexteRaw?.y, 120),
        width: pxNum(posTexteRaw?.width, 320),
        height: pxNum(posTexteRaw?.height, null),
    };

    // Logos multiples + fallback mono-logo (même logique que AfficherDevis)
    const placementsRaw =
        safeJSON(infosSociete?.logos_on_page, infosSociete?.logos_on_page) || [];
    const logosOnPage = (Array.isArray(placementsRaw) ? placementsRaw : []).map(
        (p) => {
            const w = pxNum(p.width, 140);
            const h =
                p.height != null
                    ? pxNum(p.height, 70)
                    : Math.round(w * 0.5); // fallback 2:1
            const rawSrc = p?.src || (p?.file_id ? `/api/files/${p.file_id}` : null);
            return {
                id: p.id,
                x: pxNum(p.x, 420),
                y: pxNum(p.y, 40),
                width: w,
                height: h,
                // IMPORTANT: URL absolue + ?t=JWT pour background-image
                src: rawSrc ? withToken(rawSrc) : null,
            };
        }
    );

    const monoLogo =
        !logosOnPage.length && infosSociete?.logo && infosSociete?.positionlogo
            ? {
                x: pxNum(safeJSON(infosSociete.positionlogo)?.x, 420),
                y: pxNum(safeJSON(infosSociete.positionlogo)?.y, 40),
                width: pxNum(safeJSON(infosSociete.positionlogo)?.width, 140),
                height:
                    safeJSON(infosSociete.positionlogo)?.height != null
                        ? pxNum(safeJSON(infosSociete.positionlogo)?.height, 70)
                        : Math.round(
                            pxNum(safeJSON(infosSociete.positionlogo)?.width, 140) * 0.5
                        ),
                src: withToken(String(infosSociete.logo || "")),
            }
            : null;


    async function preparePdfLayout() {
        // classes déjà utilisées pour ton CSS d'export
        document.body.classList.add("static-pdf");
        pdfRef.current?.classList.add("pdf-export");

        // on laisse le temps à React de re-render avant la capture html2pdf
        await new Promise((resolve) => setTimeout(resolve, 40));
    }


    function cleanupPdfLayout() {
        pdfRef.current?.classList.remove("pdf-export");
        document.body.classList.remove("static-pdf");
    }


    const exportPDF = async () => {
        try {
            notify.info({
                title: "Préparation",
                message: "Génération du PDF…",
            });

            await preparePdfLayout();   // 👈 important

            const filename = `facture-${facture?.numero_facture || facture?.numero || facture?.id || ""
                }.pdf`;

            const blob = await buildPdfBlobWithLastFooterAndPages(
                pdfRef.current,
                footerRef.current,
                { filename }
            );

            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);

            notify.success({
                title: "PDF prêt",
                message: "La facture a été exportée.",
            });
        } catch (e) {
            console.error("[export-facture-pdf]", e);
            notify.error({
                title: "Échec export PDF",
                message: "La génération du PDF a échoué.",
            });
        } finally {
            cleanupPdfLayout();        // 👈 toujours remettre l'état normal
        }
    };

    /* ---------- rendu ---------- */
    if (!facture) {
        return (
            <div className="text-center mt-20 text-gray-600">
                Chargement de la facture…
            </div>
        );
    }

    const { ligne1: addr1, cp, ville } = splitClientAddr(facture);
    const cpVille = [cp, ville].filter(Boolean).join(" ").trim();
    const numeroAff = facture.numero_facture || facture.numero || "—";
    // hauteur maxi confortable pour l'écran
    const TABLE_MAX_HEIGHT_SCREEN = 600;      // ce que tu as aujourd'hui

    // hauteur maxi en mode PDF avec marge de sécurité
    const TABLE_MAX_HEIGHT_PDF = 540;         // ~60 px de marge, à ajuster
    return (
        <div className="p-4">
            {/* Style local pour la barre d’actions (les hacks WYSIWYG sont gérés dans pdf.js) */}
            <style>{`
        .actions-bar :is(button, a){
          padding:10px 16px;
          line-height:1.2;
          font-size:.875rem;      /* text-sm */
          border-radius:9999px;   /* pill */
        }
      `}</style>

            {/* Barre d’actions centrée */}
            <div className="actions-bar flex flex-wrap justify-center gap-2 mb-4">
                <ActionsEmailFacture
                    facture={facture}
                    pdfElRef={pdfRef}
                    footerElRef={footerRef}
                    onPreparePdfLayout={preparePdfLayout}      // 👈 nouveau
                    onCleanupPdfLayout={cleanupPdfLayout}      // 👈 nouveau
                />

                <BoutonOnglet actif={false} onClick={exportPDF}>
                    Exporter en PDF
                </BoutonOnglet>
                <BoutonOnglet
                    actif={false}
                    onClick={() =>
                        navigate(`/creer-facture?id=${facture.id}&edit=true`)
                    }
                >
                    Modifier la facture
                </BoutonOnglet>
                <BoutonOnglet actif={false} onClick={() => navigate(`/documents`)}>
                    Retour aux documents
                </BoutonOnglet>
            </div>

            <div
                className="a4-page relative z-0"
                ref={pdfRef}
                style={{ ...themeStyleVars(tableTheme) }}
            >
                {/* En-tête (logos + bloc texte) */}
                <div key={isPdf ? "pdf" : "screen"}>
                    {/* Bloc texte société */}
                    {blocHtmlSafe && (
                        <div style={boxStyle(isPdf, posTexte, 45)}>
                            <div
                                className="ql-editor !p-0 !leading-snug"
                                style={{ overflow: "visible", maxHeight: "none" }}
                                dangerouslySetInnerHTML={{ __html: blocHtmlSafe }}
                            />
                        </div>
                    )}

                    {/* Logos multiples */}
                    {logosOnPage.length > 0 &&
                        logosOnPage.map((pl, idx) => (
                            <div
                                key={pl.id || idx}
                                data-logo
                                style={{
                                    ...boxStyle(
                                        isPdf,
                                        { x: pl.x, y: pl.y, width: pl.width, height: pl.height },
                                        10
                                    ),
                                    backgroundImage: pl.src ? `url("${pl.src}")` : undefined,
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center",
                                    backgroundSize: "contain",
                                }}
                            />
                        ))}

                    {/* Fallback mono-logo */}
                    {!logosOnPage.length && monoLogo && (
                        <div
                            data-logo
                            style={{
                                ...boxStyle(
                                    isPdf,
                                    {
                                        x: monoLogo.x,
                                        y: monoLogo.y,
                                        width: monoLogo.width,
                                        height: monoLogo.height,
                                    },
                                    10
                                ),
                                backgroundImage: monoLogo.src ? `url("${monoLogo.src}")` : undefined,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                                backgroundSize: "contain",
                            }}
                        />
                    )}
                </div>

                {/* Bloc client */}
                <div className="absolute z-20 top-[150px] right-[40px] min-w-[300px] max-w-[500px] bg-white border border-gray-300 shadow-md rounded p-4 text-sm">
                    <h2 className="text-md font-semibold mb-2">
                        {facture.client_nom || "—"}
                    </h2>
                    <div className="text-gray-700 space-y-1">
                        {addr1 && <p>{addr1}</p>}
                        {cpVille && <p>{cpVille}</p>}
                    </div>
                </div>

                {/* Numéro + date */}
                <div className="absolute top-[280px] left-[40px] w-[300px] text-sm">
                    <div className="font-bold">Facture n° {numeroAff}</div>
                    <div className="mt-1 text-xs text-gray-700">
                        {`Émise le ${formatDateFR(
                            facture.date_facture || facture.date
                        )}`}
                    </div>
                </div>

                {/* CONTENU */}
                <div className="a4-content mt-[300px] px-6">
                    {/* Intro facture (HTML protégé) */}
                    <IntroHtml htmlRaw={facture.intro_texte} />

                    <div className="mt-6 px-1">
                        <TableauStyleVertical
                            lignes={lignes}
                            lectureSeule={true}
                            totalLigne={totalLigne}
                            onReorder={() => { }}
                            pageBreakGap={48}
                            pdfBleedMm={5}
                            maxPageHeight={isPdf ? TABLE_MAX_HEIGHT_PDF : TABLE_MAX_HEIGHT_SCREEN}
                            isPdf={isPdf}
                            numeroDocument={facture?.numero || facture?.reference || ""}
                        />


                    </div>

                    <div className="mt-6 px-6">
                        <BlocTotaux
                            sousTotalHT={remiseCalc.hasRemise ? base.totalHT : null}
                            remise={
                                remiseCalc.hasRemise
                                    ? {
                                        label: remiseCalc.label,
                                        amountHT: remiseCalc.remiseHT,
                                    }
                                    : null
                            }
                            totalHT={remiseCalc.netHT}
                            tva={remiseCalc.netTVA}
                            totalTTC={remiseCalc.netTTC}
                        />
                    </div>

                    {/* Mentions / échéance */}
                    <div className="mentions-facturation mt-4 text-sm text-gray-700 leading-relaxed border-t pt-2 no-split">
                        {Number.isFinite(delaiUsed) && delaiUsed > 0 && (
                            <p
                                className="mb-2"
                                style={{ fontSize: `${mentionsFontPx}px`, lineHeight: 1.5 }}
                            >
                                Délai de paiement : <strong>{delaiUsed} jours</strong>
                                {echeanceDate ? (
                                    <>
                                        {" "}
                                        — Échéance le{" "}
                                        <strong>{formatDateFR(echeanceDate)}</strong>.
                                    </>
                                ) : null}
                            </p>
                        )}

                        {mentionsSafeHtml && (
                            <div
                                className="ql-editor !p-0"
                                style={{ fontSize: `${mentionsFontPx}px`, lineHeight: 1.5 }}
                                dangerouslySetInnerHTML={{ __html: mentionsSafeHtml }}
                            />
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="a4-footer" ref={footerRef}>
                    <FooterApercu />
                </div>
            </div>
        </div>
    );
}

/* ---------- Intro sécurisée ---------- */
function IntroHtml({ htmlRaw }) {
    const [safe, setSafe] = useState("");
    useEffect(() => {
        (async () => {
            const clean = DOMPurify.sanitize((htmlRaw || "").trim());
            const hydrated = await hydrateProtectedHtml(clean);
            setSafe(hydrated);
        })();
    }, [htmlRaw]);
    return (
        <div className="text-sm italic text-gray-700">
            {safe ? (
                <div
                    className="ql-editor !p-0"
                    dangerouslySetInnerHTML={{ __html: safe }}
                />
            ) : (
                <div style={{ minHeight: 40 }} />
            )}
        </div>
    );
}
